import React from 'react';
import type { ReactStripeElements } from 'react-stripe-elements';
import {
  CardCVCElement,
  CardExpiryElement,
  CardNumberElement,
  injectStripe,
  PostalCodeElement,
} from 'react-stripe-elements';

import type { ICreditCardSource } from '@feathr/blackbox';
import { Button, Checkbox, Form, FormElement, toast } from '@feathr/components';
import { cssVar, useToggle } from '@feathr/hooks';

import * as inputStyles from '@feathr/components/dist/Input/InputElement.css';
import * as styles from './CreditCardInput.css';

interface IProps {
  onClose: () => void;
  onSave: (source: ICreditCardSource) => Promise<void>;
}

type TProps = ReactStripeElements.InjectedStripeProps & IProps;

async function getToken(props: TProps, approved: boolean): Promise<void> {
  if (!approved) {
    toast('Please accept the terms and conditions before continuing', { type: 'error' });
    return;
  }

  try {
    const response = await props.stripe!.createToken();

    if (response.error) {
      toast(
        'The information you entered is invalid. Please correct your credit card details and try again.',
        {
          type: 'error',
        },
      );
    } else if (response.token) {
      await props.onSave({
        brand: response.token.card!.brand,
        exp_month: response.token.card!.exp_month,
        exp_year: response.token.card!.exp_year,
        id: response.token.id,
        last4: response.token.card!.last4,
      });
    }
  } catch (e) {
    toast('There was an issue adding your card.', { type: 'error' });
  }
}

function createOptions() {
  // TODO: Use global styles for CreditCardInput create option (font, colors, etc)
  return {
    style: {
      base: {
        font: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: '18px',
        color: '#424770',
        letterSpacing: '0.025em',
        '::placeholder': {
          color: '#aab7c4',
          content: 'Credit Card Number',
        },
      },
      invalid: {
        color: cssVar('--color-danger'),
      },
    },
  };
}

function CreditCardInput(props: TProps) {
  const { onClose } = props;
  const [approved, toggleApproved] = useToggle(false);

  const actions = [
    <Button
      disabled={!approved}
      key={'add'}
      onClick={async () => await getToken(props, approved)}
      type={'primary'}
    >
      Add card
    </Button>,
    <Button key={'cancel'} onClick={onClose} type={'naked'}>
      Cancel
    </Button>,
  ];

  return (
    <Form actions={actions} label={'Enter credit card details'}>
      <FormElement label={'Credit card number'}>
        <CardNumberElement className={inputStyles.input} {...createOptions()} />
      </FormElement>
      <div className={styles.inline}>
        <FormElement className={styles.expiration} label={'Expiration'}>
          <CardExpiryElement className={inputStyles.input} {...createOptions()} />
        </FormElement>
        <FormElement className={styles.postalCode} label={'Postal code'}>
          <PostalCodeElement className={inputStyles.input} {...createOptions()} />
        </FormElement>
        <FormElement className={styles.securityCode} label={'Security code'}>
          <CardCVCElement className={inputStyles.input} {...createOptions()} />
        </FormElement>
      </div>
      <div className={styles.authorize}>
        <Checkbox onChange={toggleApproved} value={approved} />

        <p>
          I authorize to charge my credit card above for agreed upon purchases. I understand that my
          information will be saved to file for future transactions on my account. I understand that
          this authorization will remain in effect until I cancel it in writing, and I agree to
          notify accounting@feathr.co in writing of any changes in my account information or
          termination of this authorization at least 15 days prior to the next billing date. If the
          above noted payment dates fall on a weekend or holiday, I understand that the payments may
          be executed on the next business day. I acknowledge that the origination of Credit Card
          transactions to my account must comply with the provisions of U.S. law. I certify that I
          am an authorized user of this Credit Card and will not dispute these scheduled
          transactions; so long as the transactions correspond to the terms indicated in this
          authorization form.
        </p>
      </div>
    </Form>
  );
}

export default injectStripe(CreditCardInput);
